import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

export type AdfNodeLike = {
	attrs?: { localId?: unknown } & Record<string, unknown>;
	content?: AdfNodeLike[];
};

const getAnchorLocalId = (suggestion: Suggestion): string | undefined => {
	if (suggestion.anchorLocalId) {
		return suggestion.anchorLocalId;
	}
	const firstToolCall = suggestion.toolCalls?.[0];
	if (!firstToolCall || !('localId' in firstToolCall.arguments)) {
		return undefined;
	}
	const { localId } = firstToolCall.arguments as { localId: unknown };
	return typeof localId === 'string' && localId ? localId : undefined;
};

const getLocalId = (node: AdfNodeLike): string | undefined =>
	typeof node.attrs?.localId === 'string' && node.attrs.localId ? node.attrs.localId : undefined;

export const orderSuggestionsByDocument = (
	suggestions: Suggestion[],
	doc: AdfNodeLike,
): Suggestion[] => {
	const suggestionsByAnchorLocalId = new Map<string, Suggestion[]>();
	for (const suggestion of suggestions) {
		const anchorLocalId = getAnchorLocalId(suggestion);
		if (anchorLocalId) {
			const anchored = suggestionsByAnchorLocalId.get(anchorLocalId) ?? [];
			anchored.push(suggestion);
			suggestionsByAnchorLocalId.set(anchorLocalId, anchored);
		}
	}

	const ordered: Suggestion[] = [];
	const placedSuggestionIds = new Set<string>();

	const visit = (node: AdfNodeLike, topLevelBlockLocalId: string | undefined) => {
		const localId = getLocalId(node);
		const anchored = localId ? suggestionsByAnchorLocalId.get(localId) : undefined;
		if (anchored && topLevelBlockLocalId) {
			for (const suggestion of anchored) {
				if (!placedSuggestionIds.has(suggestion.id)) {
					placedSuggestionIds.add(suggestion.id);
					ordered.push(suggestion);
				}
			}
		}
		node.content?.forEach((child) => visit(child, topLevelBlockLocalId));
	};

	doc.content?.forEach((topLevelBlock) => visit(topLevelBlock, getLocalId(topLevelBlock)));

	return ordered;
};
